<script setup>
import PendingPlacementsCard from '~/components/placement/PendingPlacementsCard.vue';
import AwaitingPlacementBanner from '~/components/placement/AwaitingPlacementBanner.vue';
import FormatExplainerDialog from '~/components/formats/FormatExplainerDialog.vue';
import {useSquashLevels} from "~/composables/useSquashLevels";
import {computed, ref, watch, onMounted, nextTick} from 'vue';
import LeagueCycleDivisionMatrix from "@/components/leagues/LeagueCycleDivisionMatrix";
import StandingsTable from "@/components/leagues/StandingsTable.vue";
import LeagueCycleRules from "@/components/leagues/LeagueCycleRules.vue";
import ActionMenuItem from "@/components/navigation/ActionMenuItem.vue";
import ShortLinkShareWrapper from "@/components/share/ShortLinkShareWrapper.vue";
import FixtureMatrixKey from "@/components/leagues/FixtureMatrixKey.vue";
import InformationDialog from "@/components/InformationDialog.vue";
import JoinRequestPending from "@/components/join/JoinRequestPending.vue";
import {useAuthService} from "@/composables/useAuthService";
import CompetitionPdfDialog from '~/components/competitions/CompetitionPdfDialog.vue';
import {canExportCompetitionPdf, EXPORT_PDF_LABEL} from '~/composables/useCompetitionPdf';
import {useMailer} from "~/composables/useMailer";
import {useMessaging} from "~/composables/useMessaging";
import {useLocalisedDates} from "~/composables/useLocalisedDates";
import HandicapCalculatorButton from '~/components/handicaps/HandicapCalculatorButton.vue';
import {useHandicapSettingsFor} from '~/composables/useHandicapSettingsFor';
const {shortDateTime} = useLocalisedDates();

const {sportUsesSquashLevels} = useSquashLevels();

// Presentation-only: which division pill is active (null = show all)
const activeDivisionId = ref(null);

// Grid (matrix) vs Standings view — shared across all divisions and persisted,
// mirroring the Flutter LeagueCycleDivisionView segmented control.
const divisionView = ref('matrix');

const {formatRange} = useCrudConverters();

// 'changed': a placement or join change the league page must reload for (its cycle counts
// come from the league response, not from this view's cycle fetch).
const emit = defineEmits(['changed']);
const props = defineProps({
  id: null,
  isInLeagueCycle: {
    type: Boolean,
    default: false,
  },
  visibleJoinLink: {
    type: Object,
    default: null,
  },
  // League-level admin-authored rich text, surfaced via the action strip.
  informationHtml: {
    type: String,
    default: null,
  },
});

const {user, isSuperUser, isAdminOfClub, isAuthenticated} = useAuthService();

const {t} = useI18n();

const isAdmin = (clubId) => {
  return isSuperUser.value || isAdminOfClub(clubId);
};

const {item: leagueCycle, getItem, itemPending: loading, hasItem} = useCrudApi('api/v2/leagueCycles');

// Export PDF: club admins and super admins only (owner decision, 7 Oct 2026).
const pdfOpen = ref(false);
const canExportPdf = computed(() => !!leagueCycle.value && canExportCompetitionPdf(user.value, leagueCycle.value.clubId));

// Divisions can exist before anyone is seated in them, and that renders a
// grid of nothing but headers, so "set up" means at least one seated team —
// not merely at least one division.
const cycleHasEntrants = computed(() =>
    (leagueCycle.value?.leagueCycleDivisions ?? []).some(d => (d.teams ?? []).length));

// Over already: nobody is going to be added now, so the empty state must not
// promise that an admin is still setting it up.
const cycleEnded = computed(() => {
  const end = leagueCycle.value?.endDate;
  return !!end && new Date(end) < new Date();
});


// Handicap calculator in the action row: the league's settings (null = handicaps off) + every team in the cycle
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => leagueCycle.value?.leagueId ? {leagueId: leagueCycle.value.leagueId} : null),
    computed(() => leagueCycle.value?.league?.handicapsEnabled !== false));
const handicapEntries = computed(() => (leagueCycle.value?.leagueCycleDivisions ?? []).flatMap((d) => d.teams ?? [])
    .map((t) => ({teamId: t.id, name: t.name, value: t.handicap ?? null})));

await getItem(props.id);
// Joined and waiting for an admin to place them (JOIN_SETTINGS_PLAN.md §6).
const isAwaitingPlacement = computed(() => !!leagueCycle.value?.currentUserPendingPlacement);
const placementDivisions = computed(() =>
    (leagueCycle.value?.leagueCycleDivisions ?? []).map(d => ({id: d.id, name: d.name})));

// Who is already in the cycle, for the place dialog: their players are not offered as
// team-mates, and a team left short of players can be completed.
const placementExistingTeams = computed(() => (leagueCycle.value?.leagueCycleDivisions ?? []).flatMap(d =>
    (d.teams ?? []).map(t => ({
      teamId: t.id,
      name: t.name,
      where: d.name,
      players: (t.players ?? []).map(p => ({id: p.id, fullName: p.fullName, imageFileName: p.imageFileName})),
    }))));

// The user's own pending join request for this cycle (from the auth user object).
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest} =
    useJoinRequestStatus('leagueCycle', computed(() => props.id));

const route = useRoute();

const {filtersToQuery} = useMailer();

// CHAT FUNCTIONALITY
const {entityChatLoading, entityChatErrors, createOrOpenEntityChat} = useMessaging();

// Check if user is a participant in this league cycle
const isInLeagueCycle = computed(() => {
  // Must have user and playerId
  if (!user.value?.playerId) return false;

  // Must have league cycle with divisions
  if (!leagueCycle.value?.leagueCycleDivisions?.length) return false;

  // Check if user is in any team in any division
  return leagueCycle.value.leagueCycleDivisions.some(lcd => {
    if (!lcd.teams?.length) return false;
    return lcd.teams.some(t => {
      if (!t.players?.length) return false;
      return t.players.some(p => p.id === user.value.playerId);
    });
  });
});

// Check if user can access chat (participant or club admin ONLY, not super users)
const canAccessChat = computed(() => {
  if (!isAuthenticated.value) return false;
  if (!leagueCycle.value?.clubId) return false;
  return isInLeagueCycle.value || isAdminOfClub(leagueCycle.value.clubId);
});

const handleChatClick = async () => {
  await createOrOpenEntityChat(
      {leagueCycleId: props.id, conversationId: leagueCycle.value?.conversationId},
      () => getItem(props.id)
  );
};

// Restore the saved Grid/Standings preference (client only) and persist changes.
onMounted(() => {
  if (localStorage.getItem('leaguePreferStandings') === '1') {
    divisionView.value = 'standings';
  }
});
watch(divisionView, (v) => {
  localStorage.setItem('leaguePreferStandings', v === 'standings' ? '1' : '0');
});

// Scroll to division hash on mount
onMounted(async () => {
  await nextTick();
  if (route.hash) {
    const elementId = route.hash.substring(1); // Remove the # from hash
    const element = document.getElementById(elementId);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({behavior: 'smooth', block: 'start'});
      }, 300);
    }
  }
});

</script>

<template>
  <!-- Loading state -->
  <template v-if="!hasItem && loading">
    <v-skeleton-loader class="mb-3" type="table"></v-skeleton-loader>
  </template>

  <template v-else-if="hasItem">

    <!-- Member: joined, waiting for an admin to place them -->
    <awaiting-placement-banner
        v-if="isAwaitingPlacement"
        :placement="leagueCycle.currentUserPendingPlacement"
        :entity-name="leagueCycle.name"
        @changed="getItem(id); emit('changed')"
    />

    <!-- Admin: the waiting area (the Joining card sits above, on the league page) -->
    <pending-placements-card
        v-if="isAdmin(leagueCycle.clubId)"
        :reference-type="2"
        :reference-id="leagueCycle.id"
        :club-id="leagueCycle.clubId"
        :players-per-team="leagueCycle.playersPerTeam"
        :count="leagueCycle.pendingPlacementCount ?? 0"
        :divisions="placementDivisions"
        :existing-teams="placementExistingTeams"
        :handicaps-enabled="leagueCycle.league?.handicapsEnabled === true"
        @changed="getItem(id); emit('changed')"
    />

    <!-- ======== ACTION STRIP ======== -->
    <div class="tl-action-strip mb-4">

      <!-- Export PDF, table or grid (club admins and super admins only) -->
      <button v-if="canExportPdf" class="as" @click="pdfOpen = true">
        <v-icon size="17" icon="mdi-file-pdf-box"/>
        {{ EXPORT_PDF_LABEL }}
      </button>
      <competition-pdf-dialog v-if="canExportPdf" v-model="pdfOpen" format="leagueCycle" :competition-id="leagueCycle.id" :name="leagueCycle.name"/>

      <!-- Share -->
      <short-link-share-wrapper
          :join-reference-type="2" :join-reference-id="leagueCycle.id" :can-share-join="isAdmin(leagueCycle.clubId)">
        <template #default="{ click, loading: shareLoading }">
          <button class="as" @click="click" :disabled="shareLoading">
            <v-icon size="17" icon="mdi-share-variant"/>
            Share
          </button>
        </template>
      </short-link-share-wrapper>

      <!-- View Fixtures -->
      <router-link
          class="as"
          :to="{ name: 'FixtureSearch', query: { leagueIds: [leagueCycle.leagueId], clubIds: [leagueCycle.clubId], leagueCycleIds: [id] } }"
      >
        <v-icon size="17" icon="mdi-scoreboard"/>
        View matches
      </router-link>

      <!-- Stats -->
      <router-link
          class="as"
          :to="{ name: 'LeagueCycleStats', params: { leagueId: leagueCycle.leagueId, cycleId: id } }"
      >
        <v-icon size="17" icon="mdi-chart-line"/>
        Stats
      </router-link>

      <!-- Information (admin-authored rich text) — only when content exists -->
      <format-explainer-dialog format="league" :admin="isAdmin(leagueCycle?.clubId)" :payments="leagueCycle?.entryFeeMinor != null"/>
      <information-dialog :html="informationHtml"/>

      <!-- Chat -->
      <button v-if="canAccessChat" class="as" @click="handleChatClick" :disabled="entityChatLoading">
        <v-icon size="17" icon="mdi-forum"/>
        {{ leagueCycle.conversationId ? 'Join Chat' : 'Start Chat' }}
      </button>

      <!-- Handicap calculator (handicaps on) -->
      <handicap-calculator-button action :admin="isAdmin(leagueCycle.clubId)" :settings="handicapSettings" :entries="handicapEntries"
                                  :scoring-system-id="leagueCycle?.league?.scoringSystemId ?? leagueCycle?.scoringSystemId ?? null"
                                  :noun="(leagueCycle?.playersPerTeam ?? 1) > 1 ? 'team' : 'player'"/>

      <!-- Pending request (already applied) -->
      <join-request-pending
          v-if="!isInLeagueCycle && !isAwaitingPlacement && hasPendingRequest"
          compact
          :entity-name="leagueCycle?.name"
          :cancelling="cancelPending"
          @cancel="cancelRequest"
      />

      <!-- Join League Cycle -->
      <router-link
          v-else-if="!isInLeagueCycle && !isAwaitingPlacement && visibleJoinLink"
          class="as as--join"
          :to="'/invite/' + visibleJoinLink.slug"
      >
        <v-icon size="17" icon="mdi-account-plus"/>
        Join
      </router-link>

      <div class="vsep"></div>

      <!-- Date range chip -->
      <v-menu>
        <template #activator="{ props: menuProps }">
          <span
              class="tl-chip as-chip"
              :class="leagueCycle.isActive ? 'tl-chip--ok' : ''"
              v-bind="menuProps"
              style="cursor:pointer;"
          >
            <v-icon size="14" icon="mdi-calendar"/>
            {{ formatRange(leagueCycle.startDate, leagueCycle.endDate) }}
          </span>
        </template>
        <div class="date-pop">
          <div class="date-pop__row date-pop__row--start">
            <v-icon size="16" icon="mdi-play"/>
            {{ shortDateTime(leagueCycle.startDate) }}
          </div>
          <div class="date-pop__divider">
            <v-icon size="18" icon="mdi-arrow-down"/>
          </div>
          <div class="date-pop__row date-pop__row--end">
            <v-icon size="16" icon="mdi-stop"/>
            {{ shortDateTime(leagueCycle.endDate) }}
          </div>
        </div>
      </v-menu>

      <div class="vsep"></div>

      <!-- Rules -->
      <v-menu :close-on-content-click="false" offset-y>
        <template #activator="{ props: menuProps }">
          <button class="as" v-bind="menuProps">
            <v-icon size="17" icon="mdi-format-list-checks"/>
            Rules
          </button>
        </template>
        <v-card :elevation="0" style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;">
          <league-cycle-rules
              :auto-approve="leagueCycle.autoApprove"
              :scoring-system-name="leagueCycle.scoringSystemName"
          />
        </v-card>
      </v-menu>

      <!-- Key -->
      <fixture-matrix-key/>

      <!-- Admin: Email members -->
      <template v-if="isAdmin(leagueCycle.clubId)">
        <div class="vsep"></div>
        <v-tooltip>
          <template #activator="{ props: ttProps }">
            <router-link
                class="as"
                v-bind="ttProps"
                :to="{ ...route, query: { ...route.query, mailerQuery: filtersToQuery({ clubIds: [leagueCycle.clubId], leagueIds: [leagueCycle.leagueId], leagueCycleIds: [leagueCycle.id] }) } }"
            >
              <v-icon size="17" icon="mdi-email"/>
              Email
            </router-link>
          </template>
          Email all members of {{ leagueCycle.name }}.
        </v-tooltip>
      </template>
    </div>

    <!-- ======== NOBODY IN IT YET ========
         A cycle with no seated teams rendered the view switch and then simply
         stopped, so a member saw a dead page. Say it is still being set up,
         and point an admin at the division editor. -->
    <competitions-empty-state
        v-if="!cycleHasEntrants"
        entity-type-name="box league"
        icon="mdi-view-grid-outline"
        :noun="(leagueCycle.leagueCycleDivisions ?? []).length ? 'players' : 'divisions'"
        :admin-hint="(leagueCycle.leagueCycleDivisions ?? []).length
            ? 'Seat players in a division, or share the join link and place them as they arrive.'
            : 'Set up the divisions, then seat players in them. Anyone joining with the link waits until you place them.'"
        :is-admin="isAdmin(leagueCycle.clubId)"
        :ended="cycleEnded">
      <v-btn color="primary" variant="flat" rounded="pill" size="small"
             :to="{name: 'ClubLeagueManage', params: {clubId: leagueCycle.clubId, leagueId: leagueCycle.leagueId}, query: {cycle: leagueCycle.id}}">
        Manage this cycle
      </v-btn>
    </competitions-empty-state>

    <template v-else>
    <!-- ======== MATRIX / STANDINGS VIEW SWITCH (mockups .seg; applies to all divisions) ======== -->
    <div class="tl-seg mb-4">
      <button
          class="tl-seg__opt"
          :class="{ 'tl-seg__opt--active': divisionView === 'matrix' }"
          @click="divisionView = 'matrix'"
      >
        <v-icon size="15" icon="mdi-grid"/>
        Matrix
      </button>
      <button
          class="tl-seg__opt"
          :class="{ 'tl-seg__opt--active': divisionView === 'standings' }"
          @click="divisionView = 'standings'"
      >
        <v-icon size="15" icon="mdi-format-list-numbered"/>
        Standings
      </button>
    </div>

    <!-- ======== DIVISION PILLS (show all or jump to one) ======== -->
    <div
        v-if="leagueCycle.leagueCycleDivisions && leagueCycle.leagueCycleDivisions.length > 1"
        class="div-pills"
    >
      <button
          class="pill"
          :class="{ active: activeDivisionId === null }"
          @click="activeDivisionId = null"
      >All divisions
      </button>
      <button
          v-for="lcd in leagueCycle.leagueCycleDivisions"
          :key="lcd.id"
          class="pill"
          :class="{ active: activeDivisionId === lcd.id }"
          @click="activeDivisionId = lcd.id"
      >{{ lcd.name }}
      </button>
    </div>

    <!-- ======== DIVISION CARDS ======== -->
    <section
        v-for="(lcd, lcdKey) in leagueCycle.leagueCycleDivisions"
        :key="lcd.id"
        :id="`division-${lcd.id}`"
        class="division"
        :class="{ 'division--hidden': activeDivisionId !== null && activeDivisionId !== lcd.id }"
    >
      <div class="tl-card">
        <!-- Division header bar -->
        <div class="dhead">
          <div class="dname">{{ lcd.name }}</div>
          <div class="dhead-actions">
            <!-- Admin: email division members -->
            <v-tooltip v-if="isAdmin(lcd.clubId)" location="top">
              <template #activator="{ props: ttProps }">
                <v-btn
                    v-bind="ttProps"
                    class="dhead-btn"
                    variant="text"
                    size="x-small"
                    :icon="true"
                    :to="{ ...route, query: { ...route.query, mailerQuery: filtersToQuery({ clubIds: [leagueCycle.clubId], leagueIds: [leagueCycle.leagueId], leagueCycleIds: [leagueCycle.id], leagueCycleDivisionIds: [lcd.id] }) } }"
                    color="charcoal"
                    :aria-label="`Email members of ${lcd.name}`"
                >
                  <v-icon size="18">mdi-email</v-icon>
                </v-btn>
              </template>
              Email members of {{ lcd.name }}
            </v-tooltip>
            <!-- Fullscreen toggle (matrix only) -->
            <v-tooltip v-if="divisionView === 'matrix'" text="Full screen" location="top">
              <template #activator="{ props: fsTip }">
                <v-btn
                    v-bind="fsTip"
                    variant="text"
                    size="x-small"
                    class="dhead-btn"
                    :icon="true"
                    color="charcoal"
                    aria-label="Full screen"
                    @click="lcd.fullscreen = true"
                >
                  <v-icon size="18">mdi-overscan</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </div>

        <div v-if="divisionView === 'matrix'" class="ma-3 pt-0 border rounded">
          <!-- Match grid -->
          <league-cycle-division-matrix
              :closed="!leagueCycle.open"
              v-model:fullscreen="leagueCycle.leagueCycleDivisions[lcdKey].fullscreen"
              v-model="leagueCycle.leagueCycleDivisions[lcdKey]"
              :show-squash-level="sportUsesSquashLevels(leagueCycle?.league?.sport)"
              :handicap-settings="handicapSettings"
          />
        </div>
        <!-- Standings -->
        <div v-else class="pa-3 pt-0">
          <standings-table :division="lcd" :item-limit="999" :players-per-team="leagueCycle?.playersPerTeam ?? 1"
                           :scoring-system-id="leagueCycle?.league?.scoringSystemId ?? leagueCycle?.scoringSystemId ?? null"
                           :show-squash-levels="sportUsesSquashLevels(leagueCycle?.league?.sport)"/>
        </div>
      </div>
    </section>
    </template>

  </template>
</template>

<style scoped lang="scss">
/* action strip + .as / .vsep / .as-chip are global primitives (settings.scss) */

/* date range popup */
.date-pop {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  min-width: 200px;
  overflow: hidden;
  font-size: 12px;
  font-weight: 600;
  text-align: center;

  &__row {
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &--start {
      color: rgb(var(--v-theme-success));
    }

    &--end {
      color: rgb(var(--v-theme-error));
      border-top: 1px solid rgb(var(--v-theme-borderLight));
    }
  }

  &__divider {
    padding: 6px;
    background: rgb(var(--v-theme-light));
    border-top: 1px solid rgb(var(--v-theme-borderLight));
    border-bottom: 1px solid rgb(var(--v-theme-borderLight));
    color: rgb(var(--v-theme-charcoal));
  }
}

/* ============================================================
   DIVISION PILLS  (mockups/redesign/style.css .div-pills)
   ============================================================ */
.div-pills {
  display: flex;
  gap: 8px;
  margin: 4px 0 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.pill {
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;

  &.active {
    background: rgb(var(--v-theme-text));
    border-color: rgb(var(--v-theme-text));
    color: #fff;
  }
}

/* ============================================================
   DIVISION SECTIONS
   ============================================================ */
.division {
  margin-top: 18px;

  &:first-of-type {
    margin-top: 0;
  }

  &--hidden {
    display: none;
  }
}

/* division card header bar */
.dhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px 9px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.dname {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.dhead-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.dhead-btn {
  border-radius: 10px !important;
}
</style>
